import { MIPManageInvestorsV2Context } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/MIPManageInvestorsV2Context';
import { useMIPManageContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MIPManageContext';
import { NewLeaversModalContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewLeaversModal/NewLeaversModal';
import { NewLeaverInvestorLocal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewLeaversModal/useNewLeaversModalController';
import {
	downloadLeaversInvestorsExcelTemplate,
	uploadLeaverInvestorsExcel,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import { useTranslationDefault } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { downloadFileFromBlob } from '@dealstack/frontend/shared/files/downloadFile';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { generateUUID } from '@dealstack/frontend/shared/other/react/generateUUID';
import { useContext, useEffect, useState } from 'react';

import { SecurityType } from '@dealstack/shared/lib/generated';

export type InvestorLeaverStatistics = {
	investorsCount: number;
	bySecurity: Array<InvestorLeaverStatisticsBySecurity>;
};
export type InvestorLeaverStatisticsBySecurity = {
	totalCost: number;
	totalNumber: number;
	name: string;
	securityId: string;
	type: SecurityType;
};
export const useNewLeaversInvestorsController = () => {
	const tDefault = useTranslationDefault();

	const { deal, mipData } = useMIPManageContext();
	const { investorsForm } = useContext(NewLeaversModalContext);
	const manageInvestorsControllerV2 = useContext(MIPManageInvestorsV2Context);
	const uploadInvestorsTemplate = useWrappedRequest(uploadLeaverInvestorsExcel);
	const downloadLeaversInvestorsExcelTemplateRequest = useWrappedRequest(
		downloadLeaversInvestorsExcelTemplate
	);

	const [investorsList, setInvestorsList] = useState<NewLeaverInvestorLocal[]>([]);
	const [investorsSummary, setInvestorsSummary] = useState<InvestorLeaverStatistics>();

	useEffect(() => {
		const oldData = investorsForm.getValues();
		if (oldData?.investorsListData?.length) {
			setInvestorsList(oldData.investorsListData);
		}
		if (oldData?.summary) {
			setInvestorsSummary(oldData.summary);
		}
	}, []);

	useEffect(() => {
		const payload = uploadInvestorsTemplate?.response?.payload;
		if (!payload) {
			return;
		}

		const investorsListData = payload.data;
		const investorsSummaryData = payload.statistics;
		const convertedInvestors: NewLeaverInvestorLocal[] =
			investorsListData?.map((investor) => {
				return {
					...investor,
					id: generateUUID(),
					contracts: [],
				};
			}) ?? [];

		if (convertedInvestors?.length) {
			setInvestorsList(convertedInvestors);
		}
		if (investorsSummaryData) {
			setInvestorsSummary(investorsSummaryData);
		}

		if (!payload.isValid) {
			showToastV2({
				title: tDefault('warning'),
				message: tDefault('invalid_template'),
				state: 'warning',
			});
		}
	}, [uploadInvestorsTemplate?.response?.payload]);

	useEffect(() => {
		if (!investorsSummary) {
			return;
		}
		investorsForm.setValue('summary', investorsSummary);
	}, [investorsSummary]);

	useEffect(() => {
		if (!investorsList) {
			return;
		}
		investorsForm.setValue('investorsListData', investorsList);
	}, [investorsList]);

	const onTemplateDownload = () => {
		const bulkActionPayload = manageInvestorsControllerV2.bulkActionPayload;
		const templateData = bulkActionPayload
			? { bulkAction: bulkActionPayload }
			: {
					investorId:
						manageInvestorsControllerV2.selectedInvestors
							?.filter((item) => item.isEligibleForLeavers)
							.map((item) => item.id) ?? [],
				};

		downloadLeaversInvestorsExcelTemplateRequest.fetch(templateData).onSuccess((payload) => {
			if (!payload) {
				return;
			}
			const templateName = `${deal?.name}-${mipData?.entityName ?? ''}-investors-template.xlsx`;

			downloadFileFromBlob(payload, templateName);
		});
	};

	return {
		uploadInvestorsTemplate,
		investorsList,
		setInvestorsList,
		investorsSummary,
		onTemplateDownload,
	};
};
